import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import api, { getBackendUrl } from '../../config/api';
import styled from 'styled-components';
import { theme } from '../../styles/theme';
import { 
  TruckIcon, 
  PlusIcon, 
  // EyeIcon, // Unused - removed
  PencilIcon,
  TrashIcon,
  SparklesIcon,
  MapPinIcon,
  ShieldCheckIcon,
  ShareIcon,
} from '@heroicons/react/24/outline';
import ComprehensiveVesselForm from './ComprehensiveVesselForm';
import VesselLocationModal from './VesselLocationModal';
import ShareVesselTrackingModal from './ShareVesselTrackingModal';
import toast from 'react-hot-toast';

// Styled components
const VesselsContainer = styled.div`
  min-height: 100vh;
  background: linear-gradient(135deg, ${theme.colors.background.primary} 0%, ${theme.colors.background.secondary} 100%);
  padding: 2rem;
  position: relative;
  
  /* Prevent white flash by setting background immediately */
  &::before {
    content: '';
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(135deg, ${theme.colors.background.primary} 0%, ${theme.colors.background.secondary} 100%);
    z-index: -1;
  }
`;

const VesselsHeader = styled.div`
  background: ${theme.colors.background.paper};
  border-radius: ${theme.borderRadius.lg};
  padding: 2rem;
  margin-bottom: 2rem;
  box-shadow: ${theme.shadows.sm};
  border: 1px solid ${theme.colors.border.primary};
`;

const VesselsTitle = styled.h1`
  font-size: ${theme.fontSizes['3xl']};
  font-weight: ${theme.fontWeights.bold};
  color: ${theme.colors.text.primary};
  margin: 0 0 1rem 0;
  display: flex;
  align-items: center;
  gap: 0.75rem;
`;

const VesselsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(350px, 1fr));
  gap: 1.5rem;
  margin-bottom: 2rem;
`;

const VesselCard = styled.div`
  background: ${theme.colors.background.paper};
  border: 1px solid ${theme.colors.border.primary};
  border-radius: ${theme.borderRadius.lg};
  padding: 1.5rem;
  box-shadow: ${theme.shadows.sm};
  transition: all 0.3s ease;
  cursor: pointer;
  
  &:hover {
    box-shadow: ${theme.shadows.md};
    transform: translateY(-2px);
  }
`;

const LoadingSpinner = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 200px;
  font-size: ${theme.fontSizes.lg};
  color: ${theme.colors.text.secondary};
`;

const Vessels = () => {
  // const navigate = useNavigate(); // Unused
  const { isAuthenticated } = useAuth();
  const [vessels, setVessels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedVessel, setSelectedVessel] = useState(null);
  const [vesselSeasonData, setVesselSeasonData] = useState({});
  const [showLocationModal, setShowLocationModal] = useState(false);
  const [locationModalVessel, setLocationModalVessel] = useState(null);
  const [trackingShareVessel, setTrackingShareVessel] = useState(null);
  const [createForm, setCreateForm] = useState({
    // Basic Information
    vessel_name: '',
    vessel_type: 'sailboat',
    year_built: '',
    manufacturer: '',
    model: '',
    hin: '', // Hull Identification Number
    registration_number: '',
    
    // Physical Specifications
    length_overall: '',
    beam: '',
    draft: '',
    displacement: '',
    fuel_capacity: '',
    fresh_water_capacity: '',
    holding_tank_capacity: '',
    
    // Electrical Specifications
    electrical_system: '',
    battery_bank_capacity: '',
    inverter_capacity: '',
    solar_panel_capacity: '',
    
    // Engine Information
    engine_make: '',
    engine_model: '',
    engine_serial: '',
    engine_hours: '',
    fuel_type: '',
    horsepower: '',
    
    // Additional Details
    hull_material: '',
    deck_material: '',
    interior_layout: '',
    navigation_equipment: '',
    safety_equipment: '',
    entertainment_systems: '',
    
    // Ownership Information
    purchase_date: '',
    purchase_price: '',
    current_value: '',
    insurance_company: '',
    insurance_policy_number: '',
    insurance_expiry: '',
    
    // Service Information
    last_survey_date: '',
    next_survey_due: '',
    last_haul_out: '',
    next_haul_out_due: '',
    service_provider: '',
    marina_location: '',
    
    // Custom Fields
    custom_fields: {},
    notes: '',
    
    // Images
    primary_image_url: '',
    image_urls: [],
    engines: []
  });
  const [editForm, setEditForm] = useState({
    // Basic Information
    vessel_name: '',
    vessel_type: 'sailboat',
    year_built: '',
    manufacturer: '',
    model: '',
    hin: '',
    registration_number: '',
    
    // Physical Specifications
    length_overall: '',
    beam: '',
    draft: '',
    displacement: '',
    fuel_capacity: '',
    fresh_water_capacity: '',
    holding_tank_capacity: '',
    
    // Electrical Specifications
    electrical_system: '',
    battery_bank_capacity: '',
    inverter_capacity: '',
    solar_panel_capacity: '',
    
    // Engine Information
    engine_make: '',
    engine_model: '',
    engine_serial: '',
    engine_hours: '',
    fuel_type: '',
    horsepower: '',
    
    // Additional Details
    hull_material: '',
    deck_material: '',
    interior_layout: '',
    navigation_equipment: '',
    safety_equipment: '',
    entertainment_systems: '',
    
    // Ownership Information
    purchase_date: '',
    purchase_price: '',
    current_value: '',
    insurance_company: '',
    insurance_policy_number: '',
    insurance_expiry: '',
    
    // Service Information
    last_survey_date: '',
    next_survey_due: '',
    last_haul_out: '',
    next_haul_out_due: '',
    service_provider: '',
    marina_location: '',
    
    // Custom Fields
    custom_fields: {},
    notes: '',
    
    // Images
    primary_image_url: '',
    image_urls: [],
    engines: []
  });

  useEffect(() => {
    loadVessels();
  }, []);

  // Removed excessive console.log statements that were causing infinite re-renders

  const loadVessels = async () => {
    setLoading(true);
    try {
      const response = await api.get('/api/vessels');
      const vesselsData = response.data.vessels || [];
      setVessels(vesselsData);
      // Show vessel cards immediately — season rollups load in the background
      setLoading(false);

      const seasonPromises = vesselsData.map(async (vessel) => {
        try {
          let activeSeasonId = null;
          let activeSeason = null;
          const activeSeasonResponse = await api.get(
            `/api/vessel-seasons/vessel/${vessel.id}/active`,
            { validateStatus: (s) => s === 200 || s === 404 }
          );
          if (activeSeasonResponse.status === 200) {
            activeSeason = activeSeasonResponse.data.season;
            activeSeasonId = activeSeason?.id;
          }

          let data;
          if (activeSeasonId) {
            // lite=1 skips per-trip track enrichment (card totals only)
            const summaryResponse = await api.get(
              `/api/vessel-seasons/${activeSeasonId}/summary?lite=1`
            );
            data = {
              year: summaryResponse.data.season?.year || activeSeason?.year,
              season: summaryResponse.data.season || activeSeason,
              summary: summaryResponse.data.summary || {},
              seasonId: activeSeasonId,
            };
          } else {
            const seasonResponse = await api.get(
              `/api/trips/vessel/${vessel.id}/season?lite=1`
            );
            data = {
              ...seasonResponse.data,
              seasonId: null,
            };
          }

          return { vesselId: vessel.id, data };
        } catch (error) {
          console.error(`Error loading season data for vessel ${vessel.id}:`, error);
          return { vesselId: vessel.id, data: null };
        }
      });

      const seasonResults = await Promise.all(seasonPromises);
      const seasonDataMap = {};
      seasonResults.forEach(({ vesselId, data }) => {
        if (data) {
          seasonDataMap[vesselId] = data;
        }
      });

      setVesselSeasonData(seasonDataMap);
    } catch (error) {
      console.error('Error loading vessels:', error);
      toast.error('Failed to load vessels');
      setLoading(false);
    }
  };

  const handleCreateVessel = async (e) => {
    e.preventDefault();
    try {
      // Clean the form data - convert empty strings to null for proper database handling
      const cleanedForm = { ...createForm };
      
      // Convert empty strings to null for all fields
      Object.keys(cleanedForm).forEach(key => {
        if (cleanedForm[key] === '' || cleanedForm[key] === undefined) {
          cleanedForm[key] = null;
        }
      });
      
      const response = await api.post('/api/vessels', cleanedForm);
      const data = response.data;
      toast.success('Vessel created successfully');
      setShowCreateModal(false);
      setCreateForm({
        // Basic Information
        vessel_name: '',
        vessel_type: 'sailboat',
        year_built: '',
        manufacturer: '',
        model: '',
        hin: '',
        registration_number: '',
        
        // Physical Specifications
        length_overall: '',
        beam: '',
        draft: '',
        displacement: '',
        fuel_capacity: '',
        water_capacity: '',
        holding_tank_capacity: '',
        
        // Electrical Specifications
        electrical_system: '',
        battery_bank_capacity: '',
        inverter_capacity: '',
        solar_panel_capacity: '',
        
        // Engine Information
        engine_make: '',
        engine_model: '',
        engine_serial: '',
        engine_hours: '',
        fuel_type: '',
        horsepower: '',
        
        // Additional Details
        hull_material: '',
        deck_material: '',
        interior_layout: '',
        navigation_equipment: '',
        safety_equipment: '',
        entertainment_systems: '',
        
        // Ownership Information
        purchase_date: '',
        purchase_price: '',
        current_value: '',
        insurance_company: '',
        insurance_policy_number: '',
        insurance_expiry: '',
        
        // Service Information
        last_survey_date: '',
        next_survey_due: '',
        last_haul_out: '',
        next_haul_out_due: '',
        service_provider: '',
        marina_location: '',
        
        // Custom Fields
        custom_fields: {},
        notes: '',
        
        // Images
        primary_image_url: '',
        image_urls: []
      });
      loadVessels();
    } catch (error) {
      console.error('Error creating vessel:', error);
      toast.error(error.message || 'Failed to create vessel');
    }
  };

    const handleEditVessel = async (e) => {
      e.preventDefault();
    try {
      // Clean the form data - convert empty strings to null for proper database handling
      const cleanedForm = { ...editForm };
      
      // If engines array exists but empty objects, remove
      if (Array.isArray(cleanedForm.engines)) {
        cleanedForm.engines = cleanedForm.engines
          .filter(e => e && (e.make || e.model || e.serial || e.hours || e.horsepower || e.fuel_type || e.specs || (Array.isArray(e.parts_search_history) && e.parts_search_history.length > 0)))
          .map(e => ({
            name: e.name || undefined,
            make: e.make || undefined,
            model: e.model || undefined,
            serial: e.serial || undefined,
            year: e.year === '' ? null : e.year,
            hours_path: e.hours_path || undefined,
            hours: e.hours === '' ? null : e.hours,
            horsepower: e.horsepower === '' ? null : e.horsepower,
            fuel_type: e.fuel_type || undefined,
            type: e.type || 'main',
            // Preserve AI-enriched data and search history
            specs: e.specs || undefined,
            specs_last_updated: e.specs_last_updated || undefined,
            parts_search_history: Array.isArray(e.parts_search_history) ? e.parts_search_history : undefined
          }));
        if (cleanedForm.engines.length === 0) delete cleanedForm.engines;
      }
      // Backward compat: map water_capacity -> fresh_water_capacity if present
      if (cleanedForm.water_capacity && !cleanedForm.fresh_water_capacity) {
        cleanedForm.fresh_water_capacity = cleanedForm.water_capacity;
        delete cleanedForm.water_capacity;
      }
      
      // Convert empty strings to null for all fields
      Object.keys(cleanedForm).forEach(key => {
        if (cleanedForm[key] === '' || cleanedForm[key] === undefined) {
          cleanedForm[key] = null;
        }
      });

      // Only preserve images if they weren't updated in the form
      // If editForm has images, use them; otherwise preserve existing
      if (!editForm.image_urls || editForm.image_urls.length === 0) {
        if (selectedVessel.image_urls) {
          cleanedForm.image_urls = selectedVessel.image_urls;
        }
      }
      if (!editForm.primary_image_url) {
        if (selectedVessel.primary_image_url) {
          cleanedForm.primary_image_url = selectedVessel.primary_image_url;
        }
      }
      
      console.log('🔧 Vessels: handleEditVessel images:', {
        image_urls: cleanedForm.image_urls,
        primary_image_url: cleanedForm.primary_image_url
      });
      
      const backendUrl = getBackendUrl();
      console.log('🌐 Backend URL being used:', backendUrl);
      console.log('📡 Full API URL:', `${backendUrl}/api/vessels/${selectedVessel.id}`);
      
      const token = localStorage.getItem('token');
      const response = await fetch(`${backendUrl}/api/vessels/${selectedVessel.id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(cleanedForm)
      });

      if (!response.ok) {
        let errorText = 'Failed to update vessel';
        try {
          const errorData = await response.json();
          console.error('❌ Update vessel response error:', errorData);
          errorText = errorData.message || errorData.error || errorText;
        } catch (_) {
          // ignore JSON parse error
        }
        throw new Error(errorText);
      }

      const updated = await response.json();
      toast.success('Vessel updated successfully');
      // Keep edit modal open and refresh form with server state to preserve current section
      if (updated && updated.vessel) {
        const vessel = updated.vessel;
        setSelectedVessel(vessel);
        setEditForm(prev => ({
          ...prev,
          id: vessel.id,
          engines: Array.isArray(vessel.engines) ? vessel.engines : prev.engines
        }));
      }
      // Also refresh the cards list in the background
      loadVessels();
    } catch (error) {
      console.error('Error updating vessel:', error);
      toast.error(error.message || 'Failed to update vessel');
    }
  };

  const handleDeleteVessel = async (vesselId) => {
    toast((t) => (
      <div className="flex flex-col space-y-2">
        <span className="font-medium text-red-600">Delete Vessel</span>
        <span className="text-sm text-gray-600">This action cannot be undone.</span>
        <div className="flex space-x-2">
          <button
            onClick={() => {
              toast.dismiss(t.id);
              performDeleteVessel(vesselId);
            }}
            className="px-3 py-1 bg-red-500 text-white rounded text-sm hover:bg-red-600"
          >
            Delete
          </button>
          <button
            onClick={() => toast.dismiss(t.id)}
            className="px-3 py-1 bg-gray-300 text-gray-700 rounded text-sm hover:bg-gray-400"
          >
            Cancel
          </button>
        </div>
      </div>
    ), { duration: 10000 });
    return;
  };

  const performDeleteVessel = async (vesselId) => {
    console.log('🗑️ performDeleteVessel called with vesselId:', vesselId);
    
    try {
      const token = localStorage.getItem('token');
      console.log('🔑 Token found:', token ? 'Yes' : 'No');
      
      console.log(`📡 Sending DELETE request to: /api/vessels/${vesselId}`);
      const response = await fetch(`${getBackendUrl()}/api/vessels/${vesselId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      console.log('📥 Response status:', response.status, response.statusText);

      if (!response.ok) {
        let errorText = 'Failed to delete vessel';
        try {
          const errorData = await response.json();
          console.error('❌ Response error:', errorData);
          errorText = errorData.message || errorData.error || errorText;
        } catch (_) {
          // ignore JSON parse error
        }
        throw new Error(errorText);
      }

      const successData = await response.json();
      console.log('✅ Delete successful:', successData);
      
      toast.success('Vessel deleted successfully');
      console.log('🔄 Calling loadVessels to refresh list');
      loadVessels();
    } catch (error) {
      console.error('❌ Error deleting vessel:', error);
      toast.error(error.message || 'Failed to delete vessel');
    }
  };

  const handleShowLocation = async (vessel) => {
    setLocationModalVessel(vessel);
    setShowLocationModal(true);
  };

  const handleToggleOnGuard = async (vessel) => {
    try {
      const newStatus = !vessel.onguard_enabled;
      
      const response = await api.put(`/api/vessels/${vessel.id}/onguard`, {
        enabled: newStatus
      });
      
      // Update local state
      setVessels(prev => prev.map(v => 
        v.id === vessel.id ? { ...v, onguard_enabled: newStatus } : v
      ));
      
      if (newStatus && response.data.gps_status) {
        const gpsStatus = response.data.gps_status;
        
        if (gpsStatus.available) {
          if (gpsStatus.source === 'live') {
            toast.success(
              `🛡️ OnGuard Activated\n✅ GPS Position Set (Live)\n📍 ${gpsStatus.location.latitude.toFixed(6)}°, ${gpsStatus.location.longitude.toFixed(6)}°\n🟢 Ready to Monitor`,
              { duration: 5000 }
            );
          } else if (gpsStatus.source === 'last_known') {
            toast.success(
              `🛡️ OnGuard Activated\n⚠️ GPS Position Set (Last Known)\n📍 ${gpsStatus.location.latitude.toFixed(6)}°, ${gpsStatus.location.longitude.toFixed(6)}°\n🟡 Waiting for Live GPS...`,
              { duration: 6000, style: { background: '#f59e0b', color: 'white' } }
            );
          }
        } else {
          toast.error(
            `⚠️ OnGuard Activated - NO GPS AVAILABLE\n❌ Cannot set security perimeter\n🔍 Please check SignalK connection`,
            { duration: 8000, style: { background: '#f59e0b', color: 'white' } }
          );
        }
      } else if (!newStatus) {
        toast.success(`OnGuard deactivated for ${vessel.vessel_name}`);
      }
      // Notify header to refresh indicator immediately
      window.dispatchEvent(new Event('onguard:toggled'));
    } catch (error) {
      console.error('Error toggling OnGuard:', error);
      const msg = error?.response?.data?.message || error?.response?.data?.error || error.message || 'Failed to toggle OnGuard';
      toast.error(`OnGuard error: ${msg}`);
    }
  };

  const handleAIReseach = async (vesselId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${getBackendUrl()}/api/vessels/${vesselId}/research`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to run AI research');
      }

      const data = await response.json();
      if (data.ai_research?.success) {
        toast.success(`AI research completed with ${Math.round(data.ai_research.confidence_score * 100)}% confidence`);
      } else {
        toast.error('AI research failed: ' + (data.ai_research?.error || 'Unknown error'));
      }
      loadVessels();
    } catch (error) {
      console.error('Error running AI research:', error);
      toast.error(error.message || 'Failed to run AI research');
    }
  };

  const getSignalKStatusColor = (status) => {
    switch (status) {
      case 'online': return 'text-marine-800 bg-marine-100';
      case 'recent': return 'text-marine-700 bg-marine-100';
      case 'inactive': return 'text-marine-600 bg-marine-100';
      case 'offline': return 'text-marine-700 bg-marine-100';
      default: return 'text-marine-700 bg-marine-100';
    }
  };

  const getVesselTypeIcon = (type) => {
    switch (type) {
      case 'sailboat': return '⛵';
      case 'powerboat': return '🚤';
      case 'yacht': return '🛥️';
      case 'catamaran': return '⛵';
      case 'commercial': return '🚢';
      default: return '🚤';
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-marine-50">
        <div className="text-center">
          <div className="loading-spinner mx-auto mb-4"></div>
          <p className="style={{color: '#cbd5e1'}}">Loading vessels...</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-marine-50">
        <div className="max-w-md w-full space-y-8">
          <div className="text-center">
            <div className="mx-auto h-12 w-12 flex items-center justify-center rounded-full bg-marine-100">
              <TruckIcon className="h-6 w-6 text-marine-600" />
            </div>
            <h2 className="mt-6 text-3xl font-bold text-marine-900">
              Access Denied
            </h2>
            <p className="mt-2 text-sm style={{color: '#cbd5e1'}}">
              Please log in to view your vessels
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen py-8" style={{backgroundColor: '#0f172a'}}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-3xl font-bold" style={{color: '#f8fafc'}}>Vessels</h1>
              <p className="mt-2" style={{color: '#cbd5e1'}}>Manage your boats and marine equipment</p>
            </div>
            <button 
              className="btn btn-primary"
              onClick={() => setShowCreateModal(true)}
            >
              <PlusIcon className="h-5 w-5 mr-2" />
              Add Vessel
            </button>
          </div>
        </div>

        {/* Vessels Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {vessels.map((vessel) => (
            <div key={vessel.id} className="card hover:shadow-lg transition-shadow duration-200 rounded-lg shadow-md overflow-hidden" style={{backgroundColor: '#1e293b', border: '1px solid #475569'}}>
              
              {/* Action Buttons at Top */}
              <div style={{padding: '1rem', borderBottom: `1px solid ${theme.colors.border.primary}`, backgroundColor: theme.colors.background.secondary, display: 'flex', gap: '0.5rem', justifyContent: 'flex-end'}}>
                {/* Location Button */}
                <button 
                  style={{
                    backgroundColor: theme.colors.secondary.main, 
                    color: 'white', 
                    border: 'none', 
                    padding: '0.5rem 1rem', 
                    borderRadius: theme.borderRadius.md, 
                    fontSize: theme.typography.fontSize.sm, 
                    fontWeight: theme.typography.fontWeight.medium,
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem',
                    cursor: 'pointer',
                    transition: theme.transitions.default
                  }}
                  onClick={() => handleShowLocation(vessel)}
                  title="Show vessel location"
                  onMouseEnter={(e) => e.target.style.backgroundColor = theme.colors.secondary.dark}
                  onMouseLeave={(e) => e.target.style.backgroundColor = theme.colors.secondary.main}
                >
                  <MapPinIcon className="h-4 w-4" />
                  Location
                </button>
                
                {/* OnGuard Security Toggle */}
                <button 
                  style={{
                    backgroundColor: vessel.onguard_enabled ? theme.colors.success.main : theme.colors.neutral[600],
                    color: 'white', 
                    border: 'none', 
                    padding: '0.5rem 1rem', 
                    borderRadius: theme.borderRadius.md, 
                    fontSize: theme.typography.fontSize.sm, 
                    fontWeight: theme.typography.fontWeight.medium,
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem',
                    cursor: 'pointer',
                    transition: theme.transitions.default,
                    boxShadow: vessel.onguard_enabled ? `0 0 0 2px ${theme.colors.success.light}` : 'none',
                    position: 'relative'
                  }}
                  onClick={() => handleToggleOnGuard(vessel)}
                  title={vessel.onguard_enabled ? "OnGuard Active - Click to disable" : "OnGuard Disabled - Click to enable"}
                  onMouseEnter={(e) => {
                    if (vessel.onguard_enabled) {
                      e.target.style.backgroundColor = theme.colors.success.dark;
                    } else {
                      e.target.style.backgroundColor = theme.colors.neutral[500];
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (vessel.onguard_enabled) {
                      e.target.style.backgroundColor = theme.colors.success.main;
                    } else {
                      e.target.style.backgroundColor = theme.colors.neutral[600];
                    }
                  }}
                >
                  {/* Blinking red indicator - only show when OnGuard is active */}
                  {vessel.onguard_enabled && (
                    <span 
                      style={{
                        width: '6px',
                        height: '6px',
                        backgroundColor: '#ef4444',
                        borderRadius: '50%',
                        display: 'inline-block',
                        animation: 'onguard-blink 1.5s infinite',
                        boxShadow: '0 0 4px #ef4444'
                      }}
                    />
                  )}
                  <ShieldCheckIcon className="h-4 w-4" />
                  OnGuard
                </button>
              </div>
              
              {/* Large Thumbnail */}
              <div className="relative w-full h-48 overflow-hidden" style={{backgroundColor: '#334155'}}>
                {vessel.primary_image_url ? (
                  <img 
                    src={vessel.primary_image_url.startsWith('http') 
                      ? vessel.primary_image_url 
                      : `${getBackendUrl()}${vessel.primary_image_url}`
                    } 
                    alt={vessel.vessel_name}
                    className="w-full h-full object-cover"
                    onError={(e) => {
                      e.target.style.display = 'none';
                      e.target.nextSibling.style.display = 'flex';
                    }}
                  />
                ) : null}
                <div className="w-full h-full flex items-center justify-center text-6xl" style={{backgroundColor: '#334155', display: vessel.primary_image_url ? 'none' : 'flex'}}>
                  {getVesselTypeIcon(vessel.vessel_type)}
                </div>
                {vessel.primary_image_url && (
                  <div className="absolute top-2 right-2 bg-marine-600 text-white text-xs px-2 py-1 rounded-full">
                    Primary Image
                  </div>
                )}
              </div>
              
              <div className="card-header" style={{padding: '1.5rem', borderBottom: '1px solid #e5e7eb'}}>
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-lg font-medium" style={{color: '#f8fafc'}}>
                      {vessel.vessel_name}
                    </h3>
                    <p className="text-sm capitalize" style={{color: '#cbd5e1'}}>{vessel.vessel_type} • {vessel.year_built}</p>
                  </div>
                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getSignalKStatusColor(vessel.signalk_status?.status)}`}>
                    {vessel.signalk_status?.status || 'offline'}
                  </span>
                </div>
              </div>
              
              <div className="card-body" style={{padding: '1.5rem'}}>
                <div className="space-y-4">
                  {/* Vessel Details */}
                  <div className="space-y-2">
                    {vessel.manufacturer && vessel.model && (
                      <div className="text-sm" style={{color: '#cbd5e1'}}>
                        <span className="font-medium">{vessel.manufacturer}</span> {vessel.model}
                      </div>
                    )}
                    
                    {vessel.length_overall && (
                      <div className="text-sm" style={{color: '#cbd5e1'}}>
                        Length: {vessel.length_overall}ft
                        {vessel.beam && ` × ${vessel.beam}ft`}
                      </div>
                    )}
                  </div>
                  
                  {/* AI Research Status */}
                  {vessel.ai_researched && (
                    <div className="flex items-center text-sm" style={{color: '#3b82f6'}}>
                      <SparklesIcon className="h-4 w-4 mr-2" />
                      AI Enhanced ({Math.round((vessel.ai_confidence_score || 0) * 100)}% confidence)
                    </div>
                  )}
                  
                  {/* Live feed status (signalk_latest via Cerbo plugin) */}
                  <div className="text-sm" style={{ color: '#94a3b8' }}>
                    {(() => {
                      const sk = vessel.signalk_status;
                      const paths = sk?.unique_paths ?? sk?.total_data_points ?? 0;
                      const hasFeed = paths > 0 || sk?.latest_data;
                      if (!hasFeed) {
                        return 'No live feed yet — connect the BoatWise plugin on your Cerbo';
                      }
                      const statusLabel =
                        sk?.status === 'online'
                          ? 'Live feed connected'
                          : sk?.status === 'recent'
                            ? 'Live feed — data within 24h'
                            : sk?.status === 'inactive'
                              ? 'Live feed — stale (check Cerbo connection)'
                              : 'SignalK data available';
                      return (
                        <>
                          {statusLabel}
                          {paths > 0 && ` · ${paths} paths`}
                          {sk?.latest_data && (
                            <span className="block">
                              Last update: {new Date(sk.latest_data).toLocaleString()}
                            </span>
                          )}
                        </>
                      );
                    })()}
                  </div>
                  
                  {/* Season Summary */}
                  {vesselSeasonData[vessel.id] && (
                    <div className="mt-3 p-3 rounded-lg" style={{backgroundColor: '#1f2937', border: '1px solid #374151'}}>
                      <div className="flex justify-between items-center mb-2">
                        <div className="text-xs font-medium" style={{color: '#3b82f6'}}>
                          {vesselSeasonData[vessel.id].season?.year || vesselSeasonData[vessel.id].year} Season Summary
                        </div>
                        <Link 
                          to={`/vessels/${vessel.id}/seasons/view`}
                          className="text-xs font-medium hover:underline"
                          style={{color: '#60a5fa'}}
                          onClick={() => console.log('Season data:', vesselSeasonData[vessel.id])}
                        >
                          View All Seasons →
                        </Link>
                      </div>
                      <div className="grid grid-cols-2 gap-2 text-xs" style={{color: '#cbd5e1'}}>
                        <div>
                          <span className="font-medium">Trips:</span> {vesselSeasonData[vessel.id].summary.total_trips}
                        </div>
                        <div>
                          <span className="font-medium">Distance:</span> {vesselSeasonData[vessel.id].summary.total_distance_nm.toFixed(1)} nm
                        </div>
                        <div>
                          <span className="font-medium">Fuel:</span> {vesselSeasonData[vessel.id].summary.total_fuel_used_liters.toFixed(0)}L
                        </div>
                        <div>
                          <span className="font-medium">Run time:</span> {(vesselSeasonData[vessel.id].summary.total_run_hours ?? vesselSeasonData[vessel.id].summary.total_engine_hours ?? 0).toFixed(1)}h
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
              
              <div className="card-footer" style={{padding: '1.5rem', borderTop: `1px solid ${theme.colors.border.primary}`, backgroundColor: theme.colors.background.card, borderRadius: '0 0 0.5rem 0.5rem'}}>
                <div className="flex space-x-2" style={{justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '0.5rem'}}>
                  <div className="flex space-x-2" style={{flexWrap: 'wrap', gap: '0.5rem'}}>
                  {/* External live tracking share */}
                  <button
                    className="btn btn-secondary"
                    onClick={() => setTrackingShareVessel(vessel)}
                    style={{
                      backgroundColor: theme.colors.primary.main,
                      color: 'white',
                      border: 'none',
                      padding: '0.5rem 1rem',
                      borderRadius: theme.borderRadius.md,
                      fontSize: theme.typography.fontSize.sm,
                      fontWeight: theme.typography.fontWeight.medium,
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.5rem',
                      cursor: 'pointer'
                    }}
                    title="Email a live tracking link to someone outside BoatWise"
                  >
                    <ShareIcon className="h-4 w-4" />
                    Share Tracking
                  </button>

                  {/* Location Sharing Button (friends on platform) */}
                  <button 
                    className="btn btn-secondary"
                    onClick={async () => {
                      try {
                        const response = await api.get('/api/friends/location/preferences');
                        const prefs = response.data;
                        const newShareStatus = !prefs?.share_location;
                        
                        await api.put('/api/friends/location/preferences', {
                          share_location: newShareStatus,
                          share_with_friends: newShareStatus
                        });
                        
                        toast.success(newShareStatus ? 'Location sharing enabled' : 'Location sharing disabled');
                      } catch (error) {
                        console.error('Error toggling location sharing:', error);
                        toast.error('Failed to update location sharing');
                      }
                    }}
                    style={{
                      backgroundColor: theme.colors.secondary.main, 
                      color: 'white', 
                      border: 'none', 
                      padding: '0.5rem 1rem', 
                      borderRadius: theme.borderRadius.md, 
                      fontSize: theme.typography.fontSize.sm, 
                      fontWeight: theme.typography.fontWeight.medium,
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.5rem',
                      cursor: 'pointer'
                    }}
                    title="Enable/disable location sharing with friends"
                  >
                    <MapPinIcon className="h-4 w-4" />
                    Share Location
                  </button>
                  </div>
                  
                  <div className="flex space-x-2">
                  {/* Edit Button */}
                  <button 
                    className="btn btn-secondary"
                    style={{backgroundColor: theme.colors.primary.dark, color: 'white', border: 'none', padding: '0.5rem', borderRadius: theme.borderRadius.md, fontSize: theme.typography.fontSize.sm, fontWeight: theme.typography.fontWeight.medium}}
      onClick={() => {
        setSelectedVessel(vessel);
        setEditForm({
          // Vessel ID - CRITICAL for seasons
          id: vessel.id,
          
          // Basic Information
          vessel_name: vessel.vessel_name || '',
          vessel_type: vessel.vessel_type || 'sailboat',
          year_built: vessel.year_built || '',
          manufacturer: vessel.manufacturer || '',
          model: vessel.model || '',
          hin: vessel.hin || '',
          registration_number: vessel.registration_number || '',
          
          // Physical Specifications
          length_overall: vessel.length_overall || '',
          beam: vessel.beam || '',
          draft: vessel.draft || '',
          displacement: vessel.displacement || '',
          fuel_capacity: vessel.fuel_capacity || '',
          fresh_water_capacity: vessel.fresh_water_capacity || vessel.water_capacity || '',
          holding_tank_capacity: vessel.holding_tank_capacity || '',
          
          // Electrical Specifications
          electrical_system: vessel.electrical_system || '',
          battery_bank_capacity: vessel.battery_bank_capacity || '',
          inverter_capacity: vessel.inverter_capacity || '',
          solar_panel_capacity: vessel.solar_panel_capacity || '',
          
          // Engine Information
          engine_make: vessel.engine_make || '',
          engine_model: vessel.engine_model || '',
          engine_serial: vessel.engine_serial || '',
          engine_hours: vessel.engine_hours || '',
          fuel_type: vessel.fuel_type || '',
          horsepower: vessel.horsepower || '',
          
          // Additional Details
          hull_material: vessel.hull_material || '',
          deck_material: vessel.deck_material || '',
          interior_layout: vessel.interior_layout || '',
          navigation_equipment: vessel.navigation_equipment || '',
          safety_equipment: vessel.safety_equipment || '',
          entertainment_systems: vessel.entertainment_systems || '',
          
          // Ownership Information
          purchase_date: vessel.purchase_date || '',
          purchase_price: vessel.purchase_price || '',
          current_value: vessel.current_value || '',
          insurance_company: vessel.insurance_company || '',
          insurance_policy_number: vessel.insurance_policy_number || '',
          insurance_expiry: vessel.insurance_expiry || '',
          
          // Service Information
          last_survey_date: vessel.last_survey_date || '',
          next_survey_due: vessel.next_survey_due || '',
          last_haul_out: vessel.last_haul_out || '',
          next_haul_out_due: vessel.next_haul_out_due || '',
          service_provider: vessel.service_provider || '',
          marina_location: vessel.marina_location || '',
          
          // Custom Fields
          custom_fields: vessel.custom_fields || {},
          notes: vessel.notes || '',
          
          // Images
          primary_image_url: vessel.primary_image_url || '',
          image_urls: vessel.image_urls || [],
          
          // Engines (JSONB)
          engines: Array.isArray(vessel.engines) ? vessel.engines : [],
          
          // SignalK Configuration
          signalk_host: vessel.signalk_host || '',
          signalk_port: vessel.signalk_port || 3000,
          signalk_ssl: vessel.signalk_ssl || false,
          signalk_username: vessel.signalk_username || '',
          signalk_password: vessel.signalk_password || '',
          signalk_enabled: vessel.signalk_enabled || false
        });
        setShowEditModal(true);
      }}
                    title="Edit vessel"
                  >
                    <PencilIcon className="h-4 w-4" />
                  </button>
                  
                  {!vessel.ai_researched && (
                    <button 
                      className="btn btn-warning"
                      onClick={() => handleAIReseach(vessel.id)}
                      title="Run AI research to enrich vessel data"
                      style={{backgroundColor: theme.colors.accent.main, color: 'white', border: 'none', padding: '0.5rem', borderRadius: theme.borderRadius.md}}
                    >
                      <SparklesIcon className="h-4 w-4" />
                    </button>
                  )}
                  
                  <button 
                    className="btn btn-error"
                    onClick={() => handleDeleteVessel(vessel.id)}
                    title="Delete vessel"
                    style={{backgroundColor: theme.colors.error.dark, color: 'white', border: 'none', padding: '0.5rem', borderRadius: theme.borderRadius.md, fontSize: theme.typography.fontSize.sm, fontWeight: theme.typography.fontWeight.medium}}
                  >
                    <TrashIcon className="h-4 w-4" />
                  </button>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Empty State */}
        {vessels.length === 0 && (
          <div className="text-center py-12">
            <TruckIcon className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">No vessels</h3>
            <p className="mt-1 text-sm text-gray-500">Get started by adding your first vessel.</p>
            <div className="mt-6">
              <button 
                className="btn btn-primary"
                onClick={() => setShowCreateModal(true)}
              >
                <PlusIcon className="h-5 w-5 mr-2" />
                Add Vessel
              </button>
            </div>
          </div>
        )}

        {/* Create Vessel Modal */}
        {showCreateModal && (
          <ComprehensiveVesselForm
            formData={createForm}
            setFormData={setCreateForm}
            onSubmit={handleCreateVessel}
            onCancel={() => setShowCreateModal(false)}
            title="Add New Vessel"
          />
        )}

        {/* Edit Vessel Modal */}
        {showEditModal && selectedVessel && (
          <ComprehensiveVesselForm
            formData={editForm}
            setFormData={setEditForm}
            onSubmit={handleEditVessel}
            onCancel={() => setShowEditModal(false)}
            title="Edit Vessel"
          />
        )}
        
        {/* Location Modal */}
        {showLocationModal && locationModalVessel && (
          <VesselLocationModal
            vessel={locationModalVessel}
            onClose={() => {
              setShowLocationModal(false);
              setLocationModalVessel(null);
            }}
          />
        )}

        <ShareVesselTrackingModal
          isOpen={Boolean(trackingShareVessel)}
          vessel={trackingShareVessel}
          onClose={() => setTrackingShareVessel(null)}
        />
      </div>
    </div>
  );
};

export default Vessels;